import {
  REALM_PATHS,
  INTERNAL_REALM_ID,
  SDK_TIMEOUT_MS_DEFAULT,
  SDK_TIMEOUT_RETRIES_DEFAULT,
} from './consts';
import { DebugLogger } from './utils/logger';
import { getClientTypeFromKey } from './utils/getClientTypeFromKey';

export interface ClientConfig {
  clientId: string;
  redirectUri: string;
  loginUrl: string;
  errorPage?: string;
  scope: string;
  authority: string;
  storageKey: string;
  serverBaseUrl: string;
  sdkTimeoutMs: number;
  sdkTimeoutRetries: number;
  paths: {
    authorize: string;
    token: string;
    revoke: string;
  };
}

interface RawConfig {
  realm: string;
  loginPage: string;
  errorPage?: string;
  clientId: string;
  storageKey: string;
  callbackPage: string;
  scopes: string[];
}

const clientConfigCache: Partial<Record<string, ClientConfig>> = {};
const configPromises: Partial<Record<string, Promise<RawConfig>>> = {};

/**
 * @deprecated Gets cached config synchronously. Returns null if not cached.
 * For deprecated methods only - returns null if multiple configs are cached (ambiguous).
 * @returns Cached config if exactly one exists, null otherwise
 */
export const getClientConfigCache = (): ClientConfig | null => {
  const cachedKeys = Object.keys(clientConfigCache);

  if (cachedKeys.length !== 1) {
    return null;
  }

  // Return the single cached config
  return clientConfigCache[cachedKeys[0]] ?? null;
};

/**
 * Gets cached config for a specific key synchronously. Returns undefined if not cached.
 * @internal
 */
export const getCachedConfigForKey = (
  key: string
): ClientConfig | undefined => {
  return clientConfigCache[key];
};

/**
 * Clears the client config cache. For testing purposes only.
 * @internal
 */
export const clearConfigCache = (): void => {
  for (const key in clientConfigCache) {
    delete clientConfigCache[key];
  }
  for (const key in configPromises) {
    delete configPromises[key];
  }
};

const getServerBaseUrl = (): string => {
  return window.location.origin;
};

const transformRawConfig = (rawConfig: RawConfig): ClientConfig => {
  const isInternalRealm = rawConfig.realm === INTERNAL_REALM_ID;
  const paths = isInternalRealm ? REALM_PATHS.INTERNAL : REALM_PATHS.EXTERNAL;

  const serverBaseUrl = getServerBaseUrl();

  return {
    clientId: rawConfig.clientId,
    redirectUri: `${serverBaseUrl}${rawConfig.callbackPage}`,
    loginUrl: rawConfig.loginPage,
    errorPage: rawConfig.errorPage,
    scope: rawConfig.scopes.join(' '),
    authority: serverBaseUrl,
    storageKey: rawConfig.storageKey,
    serverBaseUrl: serverBaseUrl,
    sdkTimeoutMs: SDK_TIMEOUT_MS_DEFAULT,
    sdkTimeoutRetries: SDK_TIMEOUT_RETRIES_DEFAULT,
    paths: paths,
  };
};

// Import JSON files as asset URLs
// Rspack's asset/resource loader outputs these as separate JSON files and returns the URLs
// The ?url suffix ensures Vitest also treats these as URL strings instead of JSON content
// Config files in config-runtime/ are copied from the appropriate environment before build
import advisorConfigUrl from '../config-runtime/oauth2-adviseur.json?url';
import callopsConfigUrl from '../config-runtime/oauth2-callops.json?url';
import retailConfigUrl from '../config-runtime/oauth2-retail.json?url';
import zakelijkConfigUrl from '../config-runtime/oauth2-zakelijk.json?url';

const CONFIG_URLS: Record<string, string> = {
  advisor: advisorConfigUrl,
  callops: callopsConfigUrl,
  mijnnn: retailConfigUrl,
  'mijn-nn-zakelijk-portal': zakelijkConfigUrl,
};

// Fetch configs from URLs provided by Rspack
// JSON files are separate so developers can swap them without rebuilding
const loadRawConfig = async (clientType: string): Promise<RawConfig> => {
  DebugLogger.info('Loading config for client type', clientType);

  const configUrl = CONFIG_URLS[clientType];
  if (!configUrl) {
    throw new Error(`Unknown client type: ${clientType}`);
  }

  const response = await fetch(configUrl);
  if (!response.ok) {
    DebugLogger.error('Failed to fetch config', {
      status: response.status,
      statusText: response.statusText,
      url: configUrl,
    });
    throw new Error(
      `Failed to fetch config from ${configUrl}: ${response.statusText}`
    );
  }

  DebugLogger.info('Config fetched successfully', {
    clientType,
    url: configUrl,
  });
  return response.json();
};

export const getConfig = async (key: string): Promise<ClientConfig> => {
  const normalizedKey = getClientTypeFromKey(key);
  DebugLogger.info('Requesting config for key', normalizedKey);

  const cachedConfig = clientConfigCache[normalizedKey];
  if (cachedConfig !== undefined) {
    DebugLogger.info('Returning cached config for key', normalizedKey);
    return cachedConfig;
  }

  const clientType = normalizedKey;

  // Ensure we only load each config once
  configPromises[clientType] ??= loadRawConfig(clientType);

  try {
    const rawConfig = await configPromises[clientType];
    const config = transformRawConfig(rawConfig);
    clientConfigCache[normalizedKey] = config;
    DebugLogger.info('Config loaded for key', normalizedKey);
    return config;
  } catch (error) {
    DebugLogger.error('Failed to load config', error);
    throw new Error(
      `Failed to load config for key "${normalizedKey}": ${error}`
    );
  }
};
